// web/src/components/Sash.tsx
// Diagonal corner banner — the "WIN!", "ROUT!", "CUP TIE", "DEBUT!"
// banner that sashes across a hero's top-right or top-left corner. Lifted
// straight from football-magazine cover composition. Bigger and more
// dramatic than a Sticker — used sparingly for the moment a match is
// definitively special (big win, debut, hat-trick).
//
// Anchored to a parent with `position: relative` and clipped by parent
// `overflow: hidden` (CoverHero handles both).

import type { CSSProperties } from 'react';
import { motion } from 'framer-motion';

export type SashCorner = 'tr' | 'tl' | 'br' | 'bl';
export type SashTone =
  | 'win'
  | 'loss'
  | 'draw'
  | 'milestone'
  | 'cup'
  | 'shout';   // electric yellow for visual screaming

export interface SashProps {
  label: string;
  corner?: SashCorner;
  tone?: SashTone;
  /** Animate in on mount. */
  animate?: boolean;
  style?: CSSProperties;
}

const TONE_BG: Record<SashTone, string> = {
  win:       'var(--result-win)',
  loss:      'var(--result-loss)',
  draw:      'var(--result-draw)',
  milestone: 'var(--accent-coral)',
  cup:       'var(--royal)',
  shout:     'var(--electric-yellow)',
};

const TONE_INK: Record<SashTone, string> = {
  win:       'var(--ink-on-green)',
  loss:      'var(--ink-on-green)',
  draw:      '#2a1d0c',
  milestone: 'var(--ink-on-green)',
  cup:       'var(--ink-on-green)',
  shout:     '#2a1d0c',
};

// Rotation per corner — sashes always run "diagonally inward".
const CORNER_ROTATE: Record<SashCorner, number> = {
  tr: 45,
  tl: -45,
  br: -45,
  bl: 45,
};

const CORNER_POSITION: Record<SashCorner, CSSProperties> = {
  tr: { top: 28, right: -64 },
  tl: { top: 28, left: -64 },
  br: { bottom: 28, right: -64 },
  bl: { bottom: 28, left: -64 },
};

export function Sash({
  label,
  corner = 'tr',
  tone = 'win',
  animate = true,
  style,
}: SashProps) {
  const rotate = CORNER_ROTATE[corner];
  return (
    <motion.div
      initial={animate ? { opacity: 0, scale: 0.7 } : undefined}
      animate={animate ? { opacity: 1, scale: 1 } : undefined}
      transition={{ duration: 0.5, delay: 0.2, ease: [0.34, 1.4, 0.64, 1] }}
      style={{
        position: 'absolute',
        ...CORNER_POSITION[corner],
        width: 220,
        textAlign: 'center',
        background: TONE_BG[tone],
        color: TONE_INK[tone],
        padding: '6px 0',
        transform: `rotate(${rotate}deg)`,
        boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
        zIndex: 5,
        ...style,
      }}
    >
      <div
        style={{
          fontFamily: 'var(--font-masthead)',
          fontStyle: 'italic',
          fontSize: '1.25rem',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          lineHeight: 1,
          // Inset hairline that gives the sash its "printed onto stock" quality
          textShadow: '0 1px 0 rgba(0,0,0,0.15)',
        }}
      >
        {label}
      </div>
    </motion.div>
  );
}
